import { html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import styles from './file-upload.scss?inline';
import '@principal/design-system-icons-web/paperclip';
import '@principal/design-system-icons-web/trash';
import '@principal/design-system-icons-web/upload';
import '@principal/design-system-icons-web/alert-circle';
import '../modal/modal';
import { PdsFormElement } from '../pds-form-element/PdsFormElement';
import { FilesStateConfig } from './file-upload.model';
import '../show-more/show-more';

/**
 * @summary The file upload component enables users to attach and upload files from their device.
 *
 * @fires pds-file-upload-change A custom event dispatched on change of the file input
 * @fires pds-file-upload-remove A custom event dispatched on the removal of an uploaded file
 *
 * @slot help-text Optional: Use this slot instead of the helpText property, if the help text requires additional markup.
 * @slot label Optional: Use this slot instead of the label property, if the label requires additional markup.
 */

@customElement('pds-file-upload', {
  category: 'component',
  type: 'component',
  styles,
})
export class PdsFileUpload extends PdsFormElement {
  connectedCallback() {
    super.connectedCallback();
    this.initLocalization();

    this.shadowRoot?.addEventListener('pds-modal-close', () => {
      this.closeModal();
    });
  }

  /**
   * Style variant
   * - **default** renders the standard file upload color variant
   * - **inverted** renders the file upload on dark backgrounds
   */
  @property({ type: String })
  variant: 'default' | 'inverted' = 'default';

  /** @internal */
  @query('input')
  field: HTMLInputElement;

  /**
   * List of files it will accept, separated by commas
   */
  @property({ type: String })
  accept?: string;

  /**
   * Lists the name of replaced file in single fileupload
   * @internal
   */
  @state()
  newFile?: string;

  /**
   * Lists the name of current file in single fileupload
   * @internal
   */
  @state()
  currentFile?: string;

  /**
   * Does the file upload accept multiple files?
   * Defaults to false
   */
  @property({ type: Boolean })
  multiple: boolean = false;

  /**
   * @optional
   * Size
   * Maximum file size that can be accepted in KB
   */
  @property({ type: Number })
  size: number;

  /**
   * Number of initial invalid files to display in the error modal, the rest will be viewed using a "show more" component. It will works only in case of multiple file upload.
   * Default to 5.
   */
  @property({ type: Number })
  numberOfInvalidFilesToShow: number = 5;

  /**
   * Modal state
   * @internal
   */
  @property({ type: Boolean })
  modalState: boolean;

  /**
   * Error message to display when file upload fails post-submit
   */
  @property({ type: String })
  postSubmitErrorMessage: string =
    'A problem occurred while attaching this file. Please remove and try again.';

  /**
   * Modal state for single file upload
   * @internal
   */
  @state()
  replaceModal: boolean;

  /**
   * Boolean value to display invalid file size error state.
   * @internal
   */
  @state()
  invalidSize = false;

  /**
   * Boolean value to display invalid file format error state.
   * @internal
   */
  @state()
  invalidFormat = false;

  @state()
  filesArray: FileList;

  /**
   * Files that are uploaded successfully
   * @internal
   */
  @state()
  uploadedFilesArray: FilesStateConfig[] = [];

  /**
   * This is a check to see if the fileUpload has an attached file or not.
   * @internal
   */
  @state()
  hasAttachment = false;

  /**
   * Dragging state
   * @internal
   */
  @state()
  isDragging: boolean;

  /**
   * This grabs the input element from the DOM
   * @internal
   */
  @query('.pds-c-file-upload__input')
  inputEl: HTMLElement;

  private acceptedFilesArray: string[];

  private invalidFilesArray: string[] = [];

  getFileSizeBytes() {
    return this.size * 1024;
  }

  singleOrMultipleLabel() {
    return this.multiple
      ? this.translateText('attach-files')
      : this.translateText('attach-file');
  }

  handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter' || e.code === 'Enter') {
      this.inputEl.click();
    }
  }

  override labelTemplate() {
    if (!this.multiple) {
      return html` <label
        for="${this.getFieldId()}"
        class="${this.classEl('upload-label')}"
      >
        <pds-icon-upload size="xxl"></pds-icon-upload>
        <div
          class="${this.classEl('label-text-desktop')}"
          @keydown=${this.handleKeydown}
        >
          ${unsafeHTML(
            this.translateText('drop-file-click', {
              spanClass: this.classEl('upload-link'),
              interpolation: { escapeValue: false },
            }),
          )}
        </div>
        <div class="${this.classEl('label-text-mobile')}">
          ${unsafeHTML(
            this.translateText('drop-file-tap', {
              spanClass: this.classEl('upload-link'),
              interpolation: { escapeValue: false },
            }),
          )}
        </div>
      </label>`;
    }
    return html`
      <label for="${this.getFieldId()}" class="${this.classEl('upload-label')}">
        <pds-icon-upload size="xxl"></pds-icon-upload>
        <div
          class="${this.classEl('label-text-desktop')}"
          @keydown=${this.handleKeydown}
        >
          ${unsafeHTML(
            this.translateText('drop-files-click', {
              spanClass: this.classEl('upload-link'),
              interpolation: { escapeValue: false },
            }),
          )}
        </div>
        <div class="${this.classEl('label-text-mobile')}">
          ${unsafeHTML(
            this.translateText('drop-files-tap', {
              spanClass: this.classEl('upload-link'),
              interpolation: { escapeValue: false },
            }),
          )}
        </div>
      </label>
    `;
  }

  /**
   * @internal
   */
  get classNames() {
    return {
      'is-dragging': this.isDragging === true,
      'is-disabled': this.disabled,
      [this.variant]: !!this.variant,
    };
  }

  onDragOver(e: DragEvent) {
    e.preventDefault();
    if (!this.isDragging) {
      this.isDragging = true;
    }
  }

  onDragEnd(e: DragEvent) {
    e.preventDefault();
    if (this.isDragging) {
      this.isDragging = false;
    }
  }

  protected override firstUpdated() {
    super.firstUpdated();

    this.acceptedFiles();
  }

  closeModal() {
    this.modalState = false;
    this.replaceModal = false;
    // reset the error states
    this.invalidFormat = false;
    this.invalidSize = false;
    // reset the invalid files array
    this.invalidFilesArray = [];
  }

  replaceEvent() {
    let formData = new FormData();
    this.uploadedFilesArray = [];
    formData = new FormData();
    this.uploadedFilesArray.push(this.filesArray[0]);
    formData.append(this.name, this.filesArray[0], this.filesArray[0].name);
    this.replaceModal = false;
    this.currentFile = this.newFile;

    // fire change event after file has been replaced so the file array details are accurate
    this.fireChangeEvent();
  }

  acceptedFiles() {
    this.acceptedFilesArray = this.accept?.split(',') || [];
    const cleanFileExt: string[] = [];
    this.acceptedFilesArray.forEach((file) => {
      const ext = file.slice(1);
      cleanFileExt.push(ext);
    });
    this.acceptedFilesArray = cleanFileExt;
  }

  fireChangeEvent() {
    const customEvent = new CustomEvent('pds-file-upload-change', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        summary: this.uploadedFilesArray,
      },
    });
    this.dispatchEvent(customEvent);
  }

  // Setting this to InputEvent | DragEvent required us to add a
  // lot of unnecessary logic just to make typescript happy.
  // Decided that this is ok to leave as "any".
  handleChange(e: any) {
    this.filesArray = e.dataTransfer?.files || e.target?.files;

    e.preventDefault();
    this.hasAttachment = true;
    this.modalState = false;
    let formData = new FormData();

    Array.from(this.filesArray)?.map((file: FilesStateConfig) => {
      // Perform validations on submitted files
      // if file does not have a valid extension, show error modal and do not attach file
      const fileExtension = file.name.split('.').pop() || '';
      if (this.accept && !this.acceptedFilesArray.includes(fileExtension)) {
        this.modalState = true;
        this.invalidFormat = true;
        // track which file caused the issue
        this.invalidFilesArray.push(file.name);
        // display "no files attached" message if nothing could be uploaded
        if (this.uploadedFilesArray.length === 0) {
          this.hasAttachment = false;
        }
      }
      // else if file is not a valid size, show error modal and do not attach file
      else if (this.size && this.getFileSizeBytes() <= file.size) {
        this.modalState = true;
        this.invalidSize = true;
        // track which file caused the issue
        this.invalidFilesArray.push(file.name);
        // display "no files attached" message if nothing could be uploaded
        if (this.uploadedFilesArray.length === 0) {
          this.hasAttachment = false;
        }
      }
      // if passes both of the checks, file is good to go & upload it!
      else {
        // eslint-disable-next-line no-param-reassign
        file.status = 'uploading';
        // eslint-disable-next-line no-param-reassign
        file.statusText = 'success';

        // if single file upload, reset file lists instead of adding additional
        if (!this.multiple) {
          if (this.uploadedFilesArray?.length === 1) {
            this.replaceModal = true;
            this.newFile = this.filesArray[0].name;
            return nothing;
          }
          this.uploadedFilesArray = [];
          formData = new FormData();
        }
        this.uploadedFilesArray.push(file);
        this.hasAttachment = true;
        formData.append(this.name, file, file.name);
        this.currentFile = this.filesArray[0].name;
      }
      return this.internals.setFormValue(formData);
    });

    // Only fire the event if the file is not being replaced because the change event
    // will be fired from replaceEvent() instead if the file is being replaced
    if (!this.replaceModal) {
      this.fireChangeEvent();
    }
  }

  removeFile(e: Event, fileToRemove: File) {
    e.preventDefault();
    const formData = new FormData();
    this.uploadedFilesArray = this.uploadedFilesArray.filter(
      (item) => item !== fileToRemove,
    );
    this.field.value = '';
    this.uploadedFilesArray.forEach((item) => {
      const file = item;
      formData.append(this.name, file, file.name);
    });

    this.internals.setFormValue(formData);

    if (this.uploadedFilesArray.length === 0) {
      this.hasAttachment = false;
    }

    const customEvent = new CustomEvent('pds-file-upload-remove', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: {
        summary: {
          'Removed file': fileToRemove,
          'Updated files array': this.uploadedFilesArray,
        },
      },
    });

    this.dispatchEvent(customEvent);

    this.inputEl.focus();
  }

  /**
   * Set uploadedFilesArray back to empty and clear the form value
   */
  formResetCallback() {
    this.uploadedFilesArray = [];
    this.internals.setFormValue(new FormData());
    this.hasAttachment = false;
  }

  /**
   * Appears when user tries to change the file in single file upload.
   */
  notifyUser() {
    return html`<pds-modal
      modalTitle="${this.translateText('replace-file-confirmation')}"
      openOnLoad
    >
      <div>
        <pds-text-passage>
          <div class="${this.classEl('modal-text')}">
            ${this.translateText('replace')}:
          </div>
          <ul>
            <li>
              <div class="${this.classEl('current-file')}">
                ${this.currentFile}
              </div>
            </li>
          </ul>
          <div class="${this.classEl('modal-text')}">
            ${this.translateText('with')}:
          </div>
          <ul>
            <li>
              <div class="${this.classEl('new-file')}">${this.newFile}</div>
            </li>
          </ul>
        </pds-text-passage>
      </div>
      <pds-button
        link="default"
        class="${this.classEl('close-modal')}"
        @click=${this.closeModal}
        slot="footer"
        >${this.translateText('cancel')}</pds-button
      >
      <pds-button
        variant="primary"
        @click=${this.replaceEvent}
        class="${this.classEl('replace')}"
        slot="footer"
        >${this.translateText('replace')}</pds-button
      >
    </pds-modal>`;
  }

  noFilesAttached() {
    return html`<span class="${this.classEl('file-text')}"
      >${this.translateText('no-files-attached')}</span
    >`;
  }

  invalidFormatModalContent() {
    return html`<p>${this.translateText('please-ensure-file-formats')}:</p>
      <ul>
        ${Array.from(this.acceptedFilesArray)?.map(
          (item: String) => html`<li>${item}</li>`,
        )}
      </ul>`;
  }

  invalidSizeModalContent() {
    return html`<p>
      ${this.translateText('please-ensure-file-size')} ${this.size} KB.
    </p>`;
  }

  invalidFilesList() {
    const invalidFilesArrayLength = this.invalidFilesArray.length;
    if (
      this.multiple &&
      invalidFilesArrayLength > this.numberOfInvalidFilesToShow
    ) {
      return html`${Array.from(
          this.invalidFilesArray.slice(0, this.numberOfInvalidFilesToShow),
        ).map(
          (item: String) =>
            html`<div>
              <span class="${this.classEl('invalid-file')}">
                <pds-icon-alert-circle></pds-icon-alert-circle>${item}
              </span>
            </div>`,
        )}
        <pds-show-more
          >${Array.from(
            this.invalidFilesArray.slice(
              this.numberOfInvalidFilesToShow,
              invalidFilesArrayLength,
            ),
          ).map(
            (item: String) =>
              html`<div>
                <span class="${this.classEl('invalid-file')}">
                  <pds-icon-alert-circle></pds-icon-alert-circle>${item}
                </span>
              </div>`,
          )}
        </pds-show-more>`;
    }
    return html`${Array.from(this.invalidFilesArray).map(
      (item: String) =>
        html`<div>
          <span class="${this.classEl('invalid-file')}">
            <pds-icon-alert-circle></pds-icon-alert-circle>${item}
          </span>
        </div>`,
    )}`;
  }

  preSubmitErrorModal() {
    let invalidFormatErrorContent;
    let invalidSizeErrorContent;
    if (this.invalidFormat) {
      invalidFormatErrorContent = this.invalidFormatModalContent();
    }
    if (this.invalidSize) {
      invalidSizeErrorContent = this.invalidSizeModalContent();
    }
    return html`<pds-modal
      modalTitle="${this.translateText('file-could-not-be-uploaded')}:"
      openOnLoad
    >
      <div>
        <pds-text-passage>${invalidFormatErrorContent}</pds-text-passage>
        <pds-text-passage>${invalidSizeErrorContent}</pds-text-passage>
        ${this.invalidFilesList()}
      </div>
      <pds-button variant="primary" @click=${this.closeModal} slot="footer"
        >${this.translateText('try-again')}</pds-button
      >
    </pds-modal>`;
  }

  /**
   * Handles the uploaded file styling if the file has an Error
   * @internal
   */
  hasErrorStyling(file: FilesStateConfig) {
    return html`<div class="${`${this.classEl('file-uploaded')} file-error`}">
        <span class="${this.classEl('filename')}">
          <pds-icon-alert-circle></pds-icon-alert-circle>
          <span>${file.name}</span></span
        >
        <pds-button
          variant="icon"
          size="sm"
          ariaLabel="${this.translateText('remove-attachment')} ${file.name}"
          class="${this.classEl('trash-button')}"
          @click=${(e: MouseEvent) => this.removeFile(e, file)}
          ><pds-icon-trash></pds-icon-trash
        ></pds-button>
      </div>
      <div class=${this.classEl('post-submit-error-message')}>
        ${this.postSubmitErrorMessage}
      </div>`;
  }

  /**
   * Handles the uploaded file styling if the file isDisabled
   * @internal
   */
  isDisabledStyling(file: FilesStateConfig) {
    return html`<div class="${this.classEl('file-uploaded file-is-disabled')}">
      <span class="${this.classEl('filename')}">
        <pds-icon-paperclip></pds-icon-paperclip>
        <span>${file.name}</span></span
      >
      <pds-button
        variant="icon"
        size="sm"
        ariaLabel="${this.translateText('remove-attachment')} ${file.name}"
        class="${this.classEl('trash-button')}"
        @click=${(e: MouseEvent) => this.removeFile(e, file)}
        ><pds-icon-trash></pds-icon-trash
      ></pds-button>
    </div>`;
  }

  /**
   * @internal
   * Adds styling to each uploaded file in the uploadedFileArray based on if the uploaded file has an error, is disabled, or not
   */
  uploadedFileArrayStyling(file: FilesStateConfig) {
    if (file.hasError) {
      return this.hasErrorStyling(file);
    }
    if (file.isDisabled) {
      return this.isDisabledStyling(file);
    }
    return html`<div class="${this.classEl('file-uploaded')}">
      <span class="${this.classEl('filename')}">
        <pds-icon-paperclip></pds-icon-paperclip>
        <span>${file.name}</span></span
      >
      <pds-button
        variant="icon"
        size="sm"
        ariaLabel="${this.translateText('remove-attachment')} ${file.name}"
        class="${this.classEl('trash-button')}"
        @click=${(e: MouseEvent) => this.removeFile(e, file)}
        ><pds-icon-trash></pds-icon-trash
      ></pds-button>
    </div>`;
  }

  render() {
    return html`<div class=${this.getClass()}>
      <p class="${this.classEl('attach-files-label')}">
        ${this.singleOrMultipleLabel()}
        ${this.required
          ? html`<span
              class="${this.classEl('required-indicator')}"
              aria-hidden="true"
            >
              *
            </span>`
          : nothing}
      </p>
      ${this.helpTextTemplate()}
      <div
        class="${this.classEl('upload-container')}"
        @dragover=${this.onDragOver}
        @dragend=${this.onDragEnd}
        @dragleave=${this.onDragEnd}
        @drop=${this.onDragEnd}
      >
        ${this.labelTemplate()}
        <input
          type="file"
          name="${this.name}"
          id="${this.fieldId || `${this.name}-${this.randomId}-field`}"
          class="${this.classEl('input')}"
          aria-describedby="${this.getAriaDescribedBy()}"
          accept="${ifDefined(this.accept)}"
          ?disabled="${this.disabled}"
          ?multiple="${this.multiple}"
          aria-required=${this.required ? 'true' : nothing}
          ?required=${this.required}
          @change=${(e: any) => this.handleChange(e)}
          @drop=${(e: DragEvent) => this.handleChange(e)}
        />
      </div>
      <div class="${this.classEl('file-section')}">
        ${Array.from(this.uploadedFilesArray)?.map((file: FilesStateConfig) =>
          this.uploadedFileArrayStyling(file),
        )}${this.modalState === true ? this.preSubmitErrorModal() : nothing}
        ${this.hasAttachment === false ? this.noFilesAttached() : nothing}
        ${this.replaceModal === true ? this.notifyUser() : nothing}
      </div>
    </div>`;
  }
}
